import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LegalConsentNotice } from '@/components/LegalLinks';
import { SignIn, SignUp, useAuth } from '@clerk/react';
import { Check } from 'lucide-react';
import { pendingSignupCode } from '@/lib/signupCode';
import { guessProductName, isFreeStart, signupOfferLine, usePortalContext } from '@/lib/portalContext';
import { loadClarity } from '@/lib/clarity';
import { prefetchPages } from '@/lib/lazyPage';
import { ClerkFormGuard } from '@/components/auth/ClerkFormGuard';
import { CLERK_PUBLISHABLE_KEY } from '@/lib/clerkConfig';
import { BrandGeometry } from '@/components/brand/BrandGeometry';
import { AuthBackdrop } from '@/components/brand/AuthBackdrop';

/** The website's "Book a call" link (gizmodata.com's Calendly badge). */
const BOOK_A_CALL_URL = 'https://calendly.com/philip-gizmodata/30min';

/** Clerk's form, restyled: one logo + heading (ours, above the card), and labeled, full-width
 *  social buttons above the email field — the fastest path. Auth behavior is untouched. */
const CLERK_APPEARANCE = {
  // Core 3 renamed appearance.layout → appearance.options.
  options: { socialButtonsVariant: 'blockButton' as const, socialButtonsPlacement: 'top' as const },
  // Style objects, not class names: Clerk's own styles load after ours and win a tie.
  elements: {
    header: { display: 'none' },
    logoBox: { display: 'none' },
    // One provider per row, full width ("Continue with Google"), instead of a row of 4 truncated icons.
    socialButtons: { display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' },
    socialButtonsBlockButton: {
      color: 'hsl(var(--foreground))', backgroundColor: 'hsl(var(--card))',
      border: '1px solid hsl(var(--input))',
    },
    rootBox: { width: '100%' },
    cardBox: { width: '100%', maxWidth: '28rem', boxShadow: 'none' },
  },
};

/** Why GizmoData Cloud — public cloud sign-up only. Facts as on gizmodata.com and the plans. */
const ValuePoints: React.FC<{ plansFrom: string | null; compact?: boolean }> = ({ plansFrom, compact }) => {
  const points = [
    { title: 'Managed DuckDB-powered SQL', body: 'On AWS, Google Cloud or Azure.' },
    ...(plansFrom ? [{ title: `Plans from $${Number(plansFrom)}/month`, body: 'The monthly fee comes back as compute credit.' }] : []),
    // gizmodata.com's benchmark: $0.17 of raw cloud compute; GizmoData Cloud bills compute at 2× the
    // cloud's rate (COMPUTE_COST_MULTIPLIER), so here it's about $0.34.
    { title: 'Fast', body: '1 TB TPC-H in under 90 seconds for about $0.34 of compute.' },
  ];
  return (
    <ul className={compact ? 'space-y-2 text-sm' : 'space-y-5'} data-testid={compact ? 'value-points-compact' : 'value-points'}>
      {points.map((p) => (
        <li key={p.title} className="flex gap-3">
          <Check className={`mt-0.5 shrink-0 text-primary ${compact ? 'h-4 w-4' : 'h-5 w-5'}`} aria-hidden="true" />
          <span>
            <span className="font-semibold text-foreground">{p.title}</span>
            <span className="text-muted-foreground">{compact ? ' — ' : <br />}{p.body}</span>
          </span>
        </li>
      ))}
    </ul>
  );
};

/**
 * Single landing/sign-in page that replaces the dozen-plus per-page
 * "Sign In to use X" blocks. Mounted at `/sign-in` and intentionally
 * outside the main `<Layout>` (no sidebar / header chrome) so it works
 * even when no org/project context is loaded.
 *
 * If the user is already signed in (e.g. they hit the URL by mistake)
 * we send them back to `/` — or to the `from.pathname` that the
 * `RequireSignedIn` guard captured before redirecting here.
 */
export const SignInLanding: React.FC<{ mode?: 'sign-in' | 'sign-up' }> = ({ mode = 'sign-in' }) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  // The whole path the guard captured, query and fragment included: a page like /web-ui-sign-in/<id> carries
  // its request in the query, and returning to the bare path would lose it.
  const fromLocation = (location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null)?.from;
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}`
    : '/';

  // Clerk invitation tickets (GS-016): the invitation email links here with
  // `__clerk_ticket` (+ `__clerk_status=sign_up`). That must be consumed by the
  // <SignUp> component, which reads the ticket from the URL and completes the
  // invited sign-up (email pre-verified). A normal visitor gets <SignIn>.
  const params = new URLSearchParams(location.search);
  const hasInvitationTicket = params.has('__clerk_ticket');
  // `/sign-up` (the sign-in form's "Sign up" link) renders Clerk's <SignUp> on its own route.
  // It used to point at `/sign-in#/sign-up`, which <SignIn>'s hash routing doesn't know, so the
  // link did nothing (2026-09-27, when sign-up opened).
  const showSignUp = hasInvitationTicket || mode === 'sign-up';
  const signupCode = pendingSignupCode();
  // Branding and the live sign-up offer (unauthenticated), driven by the carried code.
  const context = usePortalContext(showSignUp ? signupCode || undefined : undefined);
  const productName = context?.productName || guessProductName();
  // Browser tab: "Sign up — GizmoData Cloud" / "Sign in — GizmoData Cloud at Customer".
  useEffect(() => {
    const previous = document.title;
    document.title = `${showSignUp ? 'Sign up' : 'Sign in'} — ${productName}`;
    return () => { document.title = previous; };
  }, [showSignUp, productName]);
  // Microsoft Clarity on the signed-out pages only, when the deployment configures a project id.
  useEffect(() => { loadClarity(); }, []);
  // Fetch the signed-in app's file in idle moments while the visitor signs in, so it's there after.
  useEffect(() => { prefetchPages(); }, []);
  // Clerk sends OAuth flows that need a session here with `redirect_url` pointing back at
  // its own pages — the consent step lives on the Account Portal domain
  // (accounts.<our domain>/oauth-consent), the authorize step on the frontend API
  // (clerk.<our domain>). Honor it for this origin and any host under our own registrable
  // domain (never an arbitrary URL) — otherwise "Authorize" on the API's Swagger docs signs
  // you in and strands you on the portal home (2026-09-23).
  const clerkReturnUrl = (() => {
    const raw = params.get('redirect_url');
    if (!raw) return null;
    try {
      const u = new URL(raw, window.location.origin);
      if (u.protocol !== 'https:' && u.origin !== window.location.origin) return null;
      const baseDomain = window.location.hostname.split('.').slice(-2).join('.');
      const clerkHost = (import.meta.env.VITE_CLERK_FRONTEND_API as string | undefined)?.replace(/^https?:\/\//, '');
      const ok = u.origin === window.location.origin
        || (clerkHost && u.host === clerkHost)
        || u.hostname === baseDomain || u.hostname.endsWith('.' + baseDomain);
      return ok ? u.toString() : null;
    } catch { return null; }
  })();

  // After successful sign-in, Clerk flips isSignedIn → true asynchronously.
  // Watch for that and route the user back to where the guard intercepted — or back to
  // Clerk when it sent us here mid-OAuth (otherwise the Swagger "Authorize" never completes).
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      if (clerkReturnUrl && new URL(clerkReturnUrl).origin !== window.location.origin) {
        window.location.assign(clerkReturnUrl);
        return;
      }
      navigate(clerkReturnUrl ? new URL(clerkReturnUrl).pathname + new URL(clerkReturnUrl).search : from, { replace: true });
    }
  }, [isLoaded, isSignedIn, from, navigate, clerkReturnUrl]);

  if (isLoaded && isSignedIn) {
    // Belt-and-suspenders: if the effect hasn't fired yet, render a redirect
    // node so the page doesn't briefly show the sign-in form. Not when Clerk sent
    // us here mid-OAuth: this synchronous <Navigate to="/"> beat the effect above
    // and stranded already-signed-in users on the portal home instead of returning
    // them to Clerk's consent step (Swagger "Authorize", 2026-09-23).
    if (clerkReturnUrl) return null;
    return <Navigate to={from} replace />;
  }

  const isPublicCloud = context ? context.controlPlaneMode === 'public_cloud' : productName === 'GizmoData Cloud';
  const showValue = showSignUp && isPublicCloud && !hasInvitationTicket;
  const heading = showSignUp
    ? (isPublicCloud && isFreeStart(context) ? `Start free with ${productName}` : `Create your ${productName} account`)
    : `Sign in to ${productName}`;
  const subheading = showSignUp
    ? 'Run Hardcharger clusters and DuckLake catalogs in minutes.'
    : 'Manage your clusters, catalogs and queries.';
  const offerLine = showSignUp && !hasInvitationTicket ? signupOfferLine(context) : null;

  const form = showSignUp ? (
    <SignUp
      routing="hash"
      signInUrl="/sign-in"
      fallbackRedirectUrl={from}
      signInFallbackRedirectUrl={from}
      appearance={CLERK_APPEARANCE}
    />
  ) : (
    <SignIn
      routing="hash"
      signUpUrl="/sign-up"
      fallbackRedirectUrl={from}
      signUpFallbackRedirectUrl={from}
      appearance={CLERK_APPEARANCE}
    />
  );

  return (
    <div className="brand-auth min-h-screen bg-background px-4 py-10 lg:flex lg:items-center lg:justify-center">
      <AuthBackdrop />
      <div className={`brand-auth-frame mx-auto w-full ${showValue ? 'brand-auth-split max-w-6xl lg:grid lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch' : 'brand-auth-single max-w-lg'}`}>
        {showValue && (
          <aside className="brand-auth-benefits hidden lg:block" aria-label={`Why ${productName}`}>
            <BrandGeometry />
            <h2 className="mb-6 text-2xl font-semibold text-foreground">Why {productName}</h2>
            <ValuePoints plansFrom={context?.plansFromUsdPerMonth ?? null} />
          </aside>
        )}

        <main className="brand-auth-form mx-auto w-full max-w-md space-y-6">
          {/* One logo, one heading: Clerk's own card header is hidden (CLERK_APPEARANCE). */}
          <div className="space-y-3 text-center">
            <img src="/logos/gizmodata-logo.png" alt="GizmoData" className="mx-auto h-12 w-auto" />
            <h1 className="text-2xl font-bold" data-testid="auth-heading">{heading}</h1>
            <p className="text-sm text-muted-foreground">{subheading}</p>
            {offerLine && (
              <p className="text-sm font-medium text-green-700 dark:text-green-500" data-testid="signup-offer">
                {offerLine}
              </p>
            )}
          </div>

          <ClerkFormGuard mode={showSignUp ? 'sign-up' : 'sign-in'} publishableKey={CLERK_PUBLISHABLE_KEY}>
            {form}
          </ClerkFormGuard>

          {showValue && (
            <div className="rounded-lg border bg-muted/30 p-4 lg:hidden">
              <ValuePoints plansFrom={context?.plansFromUsdPerMonth ?? null} compact />
            </div>
          )}

          {showValue && (
            <p className="text-center text-sm text-muted-foreground">
              Questions?{' '}
              <a href={BOOK_A_CALL_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-4 hover:underline">
                Book a 30-min call
              </a>
            </p>
          )}

          <LegalConsentNotice action="signing in or creating an account" className="text-center text-xs text-muted-foreground" />
        </main>
      </div>
    </div>
  );
};
